import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { htmlSafe } from '@ember/template';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { formatCurrency } from 'tio-reimagined/format';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import Card from 'tio-reimagined/components/card';
import StatCard from 'tio-reimagined/components/stat-card';
import PageHero from 'tio-reimagined/components/page-hero';
import SectionHeader from 'tio-reimagined/components/section-header';
import {
  PhSliders,
  PhBank,
  PhGraduationCap,
  PhPiggyBank,
  PhArrowRight,
  PhLightning,
  PhWarning,
  PhCheckCircle,
  PhInfo,
  PhChartLineUp,
  PhHandshake,
} from 'ember-phosphor-icons';
import type {
  FinancialSnapshot,
  Section127Recommendation,
  LrapDiscoverySummary,
} from 'tio-employee/services/career-pathways-api';

interface OptimizerModel {
  snapshot: FinancialSnapshot;
  recommendation: Section127Recommendation;
  lrapDiscovery: LrapDiscoverySummary;
}

interface OptimizerSignature {
  Args: {
    model: OptimizerModel;
  };
}

export default class CareerPathwaysFinanceOptimizer extends Component<OptimizerSignature> {
  @tracked selectedStrategy: 'balanced' | 'loan-focused' | 'tuition-focused' = 'balanced';
  @tracked loanAllocation = 2500;
  @tracked tuitionAllocation = 2750;

  get financialSnapshot() {
    return this.args.model.snapshot;
  }

  get recommendation() {
    return this.args.model.recommendation;
  }

  get lrapDiscovery() {
    return this.args.model.lrapDiscovery;
  }

  get section127() {
    return this.financialSnapshot.section127;
  }

  get hasOutsideFundsConflict() {
    return this.lrapDiscovery.outsideFundsConflicts.length > 0;
  }

  get strategies() {
    return [
      {
        id: 'loan-focused' as const,
        name: 'Loan Focused',
        description: 'Maximize debt payoff',
        loanPercent: 80,
        tuitionPercent: 20,
        icon: PhBank,
      },
      {
        id: 'balanced' as const,
        name: 'Balanced',
        description: 'Best of both',
        loanPercent: 50,
        tuitionPercent: 50,
        icon: PhSliders,
      },
      {
        id: 'tuition-focused' as const,
        name: 'Career Focused',
        description: 'Maximize growth',
        loanPercent: 20,
        tuitionPercent: 80,
        icon: PhGraduationCap,
      },
    ];
  }

  get currentScenario() {
    return (
      this.recommendation.alternativeScenarios.find((s) => s.scenario === this.selectedStrategy) ||
      this.recommendation.projectedImpact
    );
  }

  get taxSavingsExplanation() {
    const amount = this.section127.annualLimit;
    const taxRate = 0.22; // Assumed 22% bracket
    const ficaRate = 0.0765;
    const savings = amount * (taxRate + ficaRate);
    return {
      gross: amount,
      taxSavings: Math.round(amount * taxRate),
      ficaSavings: Math.round(amount * ficaRate),
      total: Math.round(savings),
      netValue: amount,
      salaryEquivalent: Math.round(amount / (1 - taxRate - ficaRate)),
    };
  }

  widthStyle = (px: number) => {
    return htmlSafe(`width: ${px}px`);
  };

  percentStyle = (percent: number) => {
    return htmlSafe(`width: ${percent}%`);
  };

  selectStrategy = (strategyId: 'balanced' | 'loan-focused' | 'tuition-focused') => {
    this.selectedStrategy = strategyId;
    const strategy = this.strategies.find((s) => s.id === strategyId);
    if (strategy) {
      this.loanAllocation = Math.round((this.section127.annualLimit * strategy.loanPercent) / 100);
      this.tuitionAllocation = Math.round(
        (this.section127.annualLimit * strategy.tuitionPercent) / 100
      );
    }
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0 max-w-6xl mx-auto">
      {{! Hero Section }}
      <PageHero @theme="violet" @rounded="2xl">
        <:icon>
          <PhSliders @size={{32}} />
          <Heading @level={{1}} class="text-white text-2xl md:text-3xl">
            Section 127 Optimizer
          </Heading>
        </:icon>
        <:default>
          <p class="tio-fg-on-brand text-lg mb-6 max-w-2xl">
            Optimize your
            {{formatCurrency this.section127.annualLimit}}
            tax-free benefit between loan repayment and career advancement.
          </p>

          {{! Current Allocation }}
          <div class="bg-white/10 backdrop-blur rounded-xl p-4 max-w-md">
            <div class="flex items-center justify-between mb-2">
              <Text class="text-white/90 text-sm">Current Year Usage</Text>
              <Text class="text-white font-semibold">
                {{formatCurrency this.section127.yearToDateUsed}}
                /
                {{formatCurrency this.section127.annualLimit}}
              </Text>
            </div>
            <div class="h-3 bg-white/20 rounded-full overflow-hidden flex">
              <div
                class="h-full tio-bg-success"
                style={{this.widthStyle this.section127.loanAllocation}}
              ></div>
              <div
                class="h-full tio-bg-brand-accent"
                style={{this.widthStyle this.section127.tuitionAllocation}}
              ></div>
            </div>
            <div class="flex items-center justify-between mt-2 text-sm">
              <span class="tio-fg-on-brand">
                <PhBank @size={{14}} class="inline mr-1" />
                Loans:
                {{formatCurrency this.section127.loanAllocation}}
              </span>
              <span class="tio-fg-on-brand">
                <PhGraduationCap @size={{14}} class="inline mr-1" />
                Tuition:
                {{formatCurrency this.section127.tuitionAllocation}}
              </span>
            </div>
          </div>
        </:default>
      </PageHero>

      {{! Outside Funds Warning }}
      {{#if this.hasOutsideFundsConflict}}
        <div class="tio-callout tio-callout--warning mt-4">
          <div class="tio-callout__icon">
            <PhWarning @size={{24}} />
          </div>
          <div>
            <h4 class="tio-callout__title">Outside Funds Conflict Detected</h4>
            {{#each this.lrapDiscovery.outsideFundsConflicts as |conflict|}}
              <p class="text-sm mb-2">{{conflict.recommendation}}</p>
            {{/each}}
            <LinkTo @route="authenticated.reimagined.lrap">
              <Button
                @intent="default"
                @size="sm"
                @appearance="outlined"
                @icon={{PhArrowRight}}
                @iconPlacement="end"
                @label="View LRAP Details"
              />
            </LinkTo>
          </div>
        </div>
      {{/if}}

      {{! Quick Stats }}
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <StatCard
          @icon={{PhPiggyBank}}
          @value={{formatCurrency this.taxSavingsExplanation.total}}
          @label="Tax Savings"
          @trend="up"
          @trendText="vs. salary bonus"
          @color="emerald"
        />
        <StatCard
          @icon={{PhBank}}
          @value={{formatCurrency this.financialSnapshot.studentLoans.totalBalance}}
          @label="Loan Balance"
          @color="indigo"
        />
        <StatCard
          @icon={{PhChartLineUp}}
          @value="{{this.currentScenario.careerAdvancementScore}}"
          @label="Career Score"
          @color="violet"
        />
        <StatCard
          @icon={{PhHandshake}}
          @value={{formatCurrency this.financialSnapshot.employerContributions.lifetime}}
          @label="Lifetime Help"
          @color="teal"
        />
      </div>

      {{! Strategy Selection }}
      <div class="mt-8">
        <Heading @level={{2}} class="text-xl font-semibold mb-4">Choose Your Strategy</Heading>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          {{#each this.strategies as |strategy|}}
            <button
              type="button"
              class="tio-option-card
                {{if (isEqual this.selectedStrategy strategy.id) 'tio-option-card--selected'}}"
              aria-pressed={{if (isEqual this.selectedStrategy strategy.id) "true" "false"}}
              {{on "click" (fn this.selectStrategy strategy.id)}}
            >
              <div class="tio-row mb-3">
                {{#let strategy.icon as |Icon|}}
                  <div
                    class="p-2 rounded-lg
                      {{if
                        (isEqual this.selectedStrategy strategy.id)
                        'tio-bg-brand-soft'
                        'tio-bg-sunken'
                      }}"
                  >
                    <Icon
                      @size={{24}}
                      class="{{if
                          (isEqual this.selectedStrategy strategy.id)
                          'tio-fg-brand'
                          'tio-fg-secondary'
                        }}"
                    />
                  </div>
                {{/let}}
                <div>
                  <Text class="font-semibold tio-fg-primary">{{strategy.name}}</Text>
                  <Text @variant="muted" @size="sm">{{strategy.description}}</Text>
                </div>
              </div>

              <div class="tio-row-tight">
                <div class="flex-1 h-2 tio-bg-sunken rounded-full overflow-hidden flex">
                  <div
                    class="h-full tio-bg-success rounded-l-full"
                    style={{this.percentStyle strategy.loanPercent}}
                  ></div>
                  <div
                    class="h-full tio-bg-brand-accent rounded-r-full"
                    style={{this.percentStyle strategy.tuitionPercent}}
                  ></div>
                </div>
              </div>
              <div class="flex justify-between text-xs tio-fg-muted mt-1">
                <span>{{strategy.loanPercent}}% Loans</span>
                <span>{{strategy.tuitionPercent}}% Tuition</span>
              </div>
            </button>
          {{/each}}
        </div>
      </div>

      {{! Projected Impact }}
      <Card @padding="lg" class="mt-8 shadow-sm">
        <div class="flex items-center justify-between mb-4">
          <Heading @level={{3}} class="text-lg font-semibold">Projected Impact</Heading>
          <Chip @intent={{if (isEqual this.selectedStrategy "balanced") "primary" "default"}}>
            {{this.selectedStrategy}}
          </Chip>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <Text @variant="muted" @size="sm">Loan Payoff Date</Text>
            <Text class="text-xl font-semibold tio-fg-primary">
              {{this.currentScenario.loanPayoffDate}}
            </Text>
          </div>
          <div>
            <Text @variant="muted" @size="sm">Interest Saved</Text>
            <p class="tio-fg-success text-xl font-semibold">
              {{formatCurrency this.currentScenario.totalInterestSaved}}
            </p>
          </div>
          <div>
            <Text @variant="muted" @size="sm">Tax Savings</Text>
            <p class="tio-fg-info text-xl font-semibold">
              {{formatCurrency this.currentScenario.taxSavings}}
            </p>
          </div>
          <div>
            <Text @variant="muted" @size="sm">Career Advancement</Text>
            <p class="tio-fg-brand text-xl font-semibold">
              {{this.currentScenario.careerAdvancementScore}}/100
            </p>
          </div>
        </div>

        <div class="mt-6 p-4 tio-bg-sunken rounded-lg">
          <div class="tio-row-top-tight">
            <PhInfo @size={{20}} class="tio-fg-brand shrink-0 mt-0.5" />
            <Text @variant="label">{{this.recommendation.reasoning}}</Text>
          </div>
        </div>
      </Card>

      {{! Tax Arbitrage Explanation }}
      <div class="mt-8 tio-bg-success-soft rounded-xl border tio-border-success p-6">
        <SectionHeader
          @icon={{PhLightning}}
          @title="The Section 127 Advantage"
          @iconClass="tio-fg-success"
          @iconSize={{24}}
        />

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="bg-white/60 rounded-lg p-4">
            <Text class="font-medium tio-fg-secondary mb-2">If given as salary bonus:</Text>
            <div class="space-y-1 text-sm">
              <div class="flex justify-between">
                <span>Gross amount</span>
                <span>{{formatCurrency this.taxSavingsExplanation.gross}}</span>
              </div>
              <div class="flex justify-between tio-fg-danger">
                <span>- Federal tax (22%)</span>
                <span>-{{formatCurrency this.taxSavingsExplanation.taxSavings}}</span>
              </div>
              <div class="flex justify-between tio-fg-danger">
                <span>- FICA (7.65%)</span>
                <span>-{{formatCurrency this.taxSavingsExplanation.ficaSavings}}</span>
              </div>
              <div class="flex justify-between font-semibold border-t pt-1">
                <span>You receive</span>
                <span>{{formatCurrency
                    (sub
                      this.taxSavingsExplanation.gross
                      (add
                        this.taxSavingsExplanation.taxSavings this.taxSavingsExplanation.ficaSavings
                      )
                    )
                  }}</span>
              </div>
            </div>
          </div>

          <div class="bg-white/60 rounded-lg p-4 border-2 border-[var(--success-icon)]">
            <p class="tio-fg-success-body font-medium mb-2">With Section 127:</p>
            <div class="space-y-1 text-sm">
              <div class="flex justify-between">
                <span>Gross amount</span>
                <span>{{formatCurrency this.taxSavingsExplanation.gross}}</span>
              </div>
              <div class="tio-fg-success flex justify-between">
                <span>- Federal tax</span>
                <span>$0</span>
              </div>
              <div class="tio-fg-success flex justify-between">
                <span>- FICA</span>
                <span>$0</span>
              </div>
              <div class="tio-fg-success-body flex justify-between font-semibold border-t pt-1">
                <span>You receive (tax-free!)</span>
                <span>{{formatCurrency this.taxSavingsExplanation.netValue}}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="tio-fg-success-body mt-4 tio-row-tight">
          <PhCheckCircle @size={{20}} />
          <Text class="font-medium">
            You gain
            {{formatCurrency this.taxSavingsExplanation.total}}
            more value compared to a salary bonus!
          </Text>
        </div>
      </div>

      {{! Recommended Actions }}
      <div class="mt-8">
        <Heading @level={{2}} class="text-xl font-semibold mb-4">Recommended Actions</Heading>

        <div class="space-y-3">
          {{#each this.recommendation.actions as |action|}}
            <div class="bg-white rounded-lg border tio-border-default p-4 tio-row-loose">
              <div
                class="p-2 rounded-lg
                  {{if (isEqual action.priority 'high') 'tio-bg-brand-soft' 'tio-bg-sunken'}}"
              >
                {{#if (isEqual action.priority "high")}}
                  <PhLightning @size={{20}} class="tio-fg-brand" />
                {{else}}
                  <PhCheckCircle @size={{20}} class="tio-fg-secondary" />
                {{/if}}
              </div>
              <div class="flex-1">
                <Text class="font-medium tio-fg-primary">{{action.title}}</Text>
                <Text @variant="muted" @size="sm">{{action.description}}</Text>
              </div>
              <Chip @intent={{if (isEqual action.priority "high") "primary" "default"}} @size="sm">
                {{action.impact}}
              </Chip>
            </div>
          {{/each}}
        </div>
      </div>

      {{! CTA }}
      <div class="mt-8 flex justify-center gap-4">
        <LinkTo @route="authenticated.reimagined.lrap">
          <Button @intent="default" @appearance="outlined">
            View LRAP Options
          </Button>
        </LinkTo>
        <Button
          @intent="primary"
          @icon={{PhArrowRight}}
          @iconPlacement="end"
          @label="Apply This Strategy"
        />
      </div>
    </VStack>
  </template>
}

// Helper functions for template
function isEqual(a: unknown, b: unknown): boolean {
  return a === b;
}

function add(a: number, b: number): number {
  return a + b;
}

function sub(a: number, b: number): number {
  return a - b;
}
